import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  GridView,
  ViewList,
  ChevronRight,
  CloudUpload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';

const ClientsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadClients();
  }, [searchQuery, filter]);

  const loadClients = async () => {
    setLoading(true);
    setTimeout(() => {
      const mockClients = [
        {
          id: 1,
          initial: 'NT',
          name: 'Northwind Trading Ltd',
          industry: 'Wholesale',
          vatNumber: 'IE3284561AH',
          balance: 18420,
          pendingDocs: 3,
          status: 'Active',
          accountant: 'Aoife Byrne',
        },
        {
          id: 2,
          initial: 'HS',
          name: 'Helix Software BV',
          industry: 'SaaS',
          vatNumber: 'NL5294716dB1',
          balance: -2400,
          pendingDocs: 0,
          status: 'Active',
          accountant: 'Marcus Klein',
        },
        {
          id: 3,
          initial: 'BC',
          name: 'Brightside Café Group',
          industry: 'Hospitality',
          vatNumber: 'IE9428374KL',
          balance: 9320,
          pendingDocs: 7,
          status: 'Active',
          accountant: 'Aoife Byrne',
        },
        {
          id: 4,
          initial: 'ML',
          name: 'Meridian Logistics',
          industry: 'Transport',
          vatNumber: 'IE5367281MM',
          balance: 44210,
          pendingDocs: 2,
          status: 'Active',
          accountant: 'Priya Nair',
        },
        {
          id: 5,
          initial: 'VL',
          name: 'Verdant Landscaping',
          industry: 'Services',
          vatNumber: 'IE1162746VG',
          balance: 1280,
          pendingDocs: 5,
          status: 'Active',
          accountant: 'Tomás Walsh',
        },
        {
          id: 6,
          initial: 'AC',
          name: 'Atlas Construction Co',
          industry: 'Construction',
          vatNumber: 'IE7741928AC',
          balance: 87600,
          pendingDocs: 12,
          status: 'Active',
          accountant: 'Priya Nair',
        },
        {
          id: 7,
          initial: 'LH',
          name: 'Lumen Health Clinics',
          industry: 'Healthcare',
          vatNumber: 'IE9201912LH',
          balance: 0,
          pendingDocs: 1,
          status: 'Active',
          accountant: 'Marcus Klein',
        },
        {
          id: 8,
          initial: 'CS',
          name: 'Cobalt Studios',
          industry: 'Creative',
          vatNumber: 'IE1289457CS',
          balance: 6750,
          pendingDocs: 0,
          status: 'Onboarding',
          accountant: 'Tomás Walsh',
        },
        {
          id: 9,
          initial: 'SP',
          name: 'Seabreeze Properties',
          industry: 'Real Estate',
          vatNumber: 'IE8812605P',
          balance: 32100,
          pendingDocs: 4,
          status: 'Active',
          accountant: 'Aoife Byrne',
        },
        {
          id: 10,
          initial: 'PC',
          name: 'Pinnacle Consulting',
          industry: 'Consulting',
          vatNumber: 'IE5502211PC',
          balance: 14900,
          pendingDocs: 2,
          status: 'Active',
          accountant: 'Priya Nair',
        },
        {
          id: 11,
          initial: 'OF',
          name: 'Orchard Foods Ltd',
          industry: 'Manufacturing',
          vatNumber: 'IE8829017OF',
          balance: 23400,
          pendingDocs: 9,
          status: 'Active',
          accountant: 'Marcus Klein',
        },
        {
          id: 12,
          initial: 'QL',
          name: 'Quartz Legal LLP',
          industry: 'Legal',
          vatNumber: 'IE3318200L',
          balance: -1200,
          pendingDocs: 0,
          status: 'Active',
          accountant: 'Tomás Walsh',
        },
      ];
      setClients(mockClients);
      setLoading(false);
    }, 500);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Onboarding':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Inactive':
        return { bg: '#F5F5F5', color: '#666' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.vatNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.industry.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter =
      filter === 'all' ||
      (filter === 'active' && client.status === 'Active') ||
      (filter === 'onboarding' && client.status === 'Onboarding') ||
      (filter === 'inactive' && client.status === 'Inactive');

    return matchesSearch && matchesFilter;
  });

  const filterCounts = {
    all: clients.length,
    active: clients.filter((c) => c.status === 'Active').length,
    onboarding: clients.filter((c) => c.status === 'Onboarding').length,
    inactive: clients.filter((c) => c.status === 'Inactive').length,
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('clients')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {clients.length} {t('client_companies_across_firm')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5568d3',
                  backgroundColor: 'rgba(102, 126, 234, 0.04)',
                },
              }}
            >
              {t('import')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/accountant-portal/clients/create')}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                },
              }}
            >
              {t('new_client')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={t('search_by_name_vat_industry')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 300,
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${t('all')} ${filterCounts.all}`}
              onClick={() => setFilter('all')}
              sx={{
                backgroundColor: filter === 'all' ? '#667eea' : 'white',
                color: filter === 'all' ? 'white' : '#666',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filter === 'all' ? '#5568d3' : '#f5f5f5',
                },
              }}
            />
            <Chip
              label={`${t('active')} ${filterCounts.active}`}
              onClick={() => setFilter('active')}
              sx={{
                backgroundColor: filter === 'active' ? '#667eea' : 'white',
                color: filter === 'active' ? 'white' : '#666',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filter === 'active' ? '#5568d3' : '#f5f5f5',
                },
              }}
            />
            <Chip
              label={`${t('onboarding')} ${filterCounts.onboarding}`}
              onClick={() => setFilter('onboarding')}
              sx={{
                backgroundColor: filter === 'onboarding' ? '#667eea' : 'white',
                color: filter === 'onboarding' ? 'white' : '#666',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filter === 'onboarding' ? '#5568d3' : '#f5f5f5',
                },
              }}
            />
            <Chip
              label={`${t('inactive')} ${filterCounts.inactive}`}
              onClick={() => setFilter('inactive')}
              sx={{
                backgroundColor: filter === 'inactive' ? '#667eea' : 'white',
                color: filter === 'inactive' ? 'white' : '#666',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filter === 'inactive' ? '#5568d3' : '#f5f5f5',
                },
              }}
            />
          </Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grid">
              <GridView fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredClients.map((client) => {
              const statusColor = getStatusColor(client.status);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={client.id}>
                  <Card
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => navigate(`/accountant-portal/clients/${client.id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '8px',
                            backgroundColor: '#E3F2FD',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            color: '#1976d2',
                          }}
                        >
                          {client.initial}
                        </Box>
                        <IconButton size="small">
                          <ChevronRight />
                        </IconButton>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {client.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {client.industry}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                        {client.vatNumber}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {t('balance')}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: client.balance < 0 ? '#F44336' : client.balance > 0 ? '#000' : '#666',
                            }}
                          >
                            €{client.balance.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="textSecondary">
                            {t('pending_docs')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {client.pendingDocs}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Chip
                          label={client.status}
                          size="small"
                          icon={<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColor.color, ml: 1 }} />}
                          sx={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.color,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {client.accountant}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!loading && filteredClients.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="textSecondary">
              {t('no_clients_found')}
            </Typography>
          </Box>
        )}
      </Box>
    </AccountantLayout>
  );
};

export default ClientsListPage;
